import { Fragment, memo } from 'react';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { SonFormVM } from 'front-office/view-models/certificate/son-form';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import InputKeyboard from 'shell/components/shared/input-keyboard';
import ListGender from 'shell/components/shared/gender';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import MunicipalityList from 'shell/components/shared/municipality-list';
import ResidenceItalian from './residence-italian';
import ResidenceForeign from './residence-foreign';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import ForeignStateList from 'shell/components/shared/foreign-state-list';

interface SonProps {
  son?: SonFormVM;
  index: number;
  onFieldSonChange: (
    index: number,
    key: keyof SonFormVM,
    value: SonFormVM[keyof SonFormVM]
  ) => void;
  onFieldsSonChange: (index: number, values: SonFormVM) => void;
}

const SonComponent = ({
  son,
  onFieldSonChange,
  index,
  onFieldsSonChange,
}: SonProps): React.ReactElement => {
  const onChange = useEventCallback((key: keyof SonFormVM, value: SonFormVM[keyof SonFormVM]) => {
    onFieldSonChange(index, key, value);
  });

  const onChanges = useEventCallback((values: SonFormVM) => {
    onFieldsSonChange(index, values);
  });

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Figlio</PmiTitleDivider>
      </Grid>
      <Grid item xs={12} md={9}>
        <InputKeyboard
          label="Cognome"
          id="surname"
          aria-label="Cognome"
          onChange={(value) => onChange('surname', value)}
          value={son?.surname || ''}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="withoutSurname"
              checked={son?.withoutSurname || false}
              onChange={() => onChange('withoutSurname', !son?.withoutSurname)}
              inputProps={{ 'aria-label': 'Persona senza cognome' }}
            />
          }
          label="Persona senza cognome"
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <InputKeyboard
          label="Nome"
          id="name"
          aria-label="Nome"
          onChange={(value) => onChange('name', value)}
          value={son?.name || ''}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="withoutName"
              checked={son?.withoutName || false}
              onChange={() => onChange('withoutName', !son?.withoutName)}
              inputProps={{ 'aria-label': 'Persona senza nome' }}
            />
          }
          label="Persona senza nome"
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <ListGender
          value={son?.gender || ''}
          onChange={(event) => onChange('gender', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Nato il"
          aria-label="Nato il"
          value={son?.dateBirth || null}
          onChange={(date) => {
            onChange('dateBirth', date);
          }}
          renderInput={(params) => (
            <PmiTextField {...params} id="dateBirth" fullWidth helperText="" />
          )}
        />
      </Grid>
      {son?.isBirthStateItalian ? (
        <Grid item xs={12} md={4}>
          <MunicipalityList
            value={son?.municipalityOfBirth || null}
            onChange={(municipality) => onChange('municipalityOfBirth', municipality)}
            label="Comune nascita"
            id="municipalityOfBirth"
            fromDate={son?.dateBirth}
          />
        </Grid>
      ) : (
        <Fragment>
          <Grid item xs={12} md={4}>
            <ForeignStateList
              value={son?.stateForeignOfBirth || null}
              onChange={(state) => onChange('stateForeignOfBirth', state)}
              label="Stato estero di nascita"
              id="foreignStateBirth"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MunicipalityForeignList
              value={son?.municipalityOfBirth || null}
              onChange={(municipality) => onChange('municipalityOfBirth', municipality)}
              label="Comune estero di nascita"
              id="municipalityForeignOfBirth"
              foreignStateIstatCode={son?.stateForeignOfBirth?.istatCode || ''}
            />
          </Grid>
        </Fragment>
      )}
      <Grid item xs={12} md={3}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato di nascita"
            id="birthState"
            value={son?.isBirthStateItalian || false}
            onChange={() =>
              onChanges({
                isBirthStateItalian: !son?.isBirthStateItalian,
                municipalityOfBirth: undefined,
                stateForeignOfBirth: undefined,
              })
            }
          >
            <FormControlLabel value="true" control={<Radio />} label="Italia" />
            <FormControlLabel value="false" control={<Radio />} label="Estero" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="residenza"
            value={son?.isResidenceItalian || false}
            id="isResidenceItalian"
            onChange={() =>
              onChanges({
                isResidenceItalian: !son?.isResidenceItalian,
                foreignStateResidence: undefined,
                foreignMunicipalityResidence: undefined,
                italianMunicipalityResidence: undefined,
                italianLocalityResidence: undefined,
              })
            }
          >
            <FormControlLabel value="true" control={<Radio />} label="Residenza Italia" />
            <FormControlLabel value="false" control={<Radio />} label="Residenza Estero" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {son?.isResidenceItalian ? (
        <Grid item xs={12}>
          <ResidenceItalian onChange={onChange} son={son} />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <ResidenceForeign onChange={onChange} son={son} />
        </Grid>
      )}
      <Grid item xs={12}>
        <PmiTextField
          id="address-residence"
          label="Indirizzo residenza"
          aria-label="Indirizzo residenza"
          fullWidth
          inputProps={{
            maxLength: '50',
          }}
          value={son?.addressResidence || ''}
          onChange={(event) => onChange('addressResidence', event.target.value)}
        />
      </Grid>
    </Grid>
  );
};

SonComponent.displayName = 'Widower with children component';

const Son = memo(SonComponent);

export default Son;
